import React, { FC } from "react";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import AccountWrap from "..";
import Button from "app/components/Button/ButtonVariant";
import SubscriptionCancelModal from "app/components/subscription/Modals/SubscriptionCancelModal";
import { useSubscriptionModel } from "./indexModel";
import { useHistory } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Loading } from "app/components/Wrappers";
import CancelProTrialSubscriptionModal from "app/components/subscription/Modals/CancelProTrialSubscription";
import { hasActiveProTrial, isStripeUser } from "./helper";
import { SubscriptionModel } from "app/models";
import {
  canCancelLicensedPlan,
  hasExpiredCoachPlusTrial,
  hasLicensedPlan,
} from "app/utils/subscription";

const Subscriptions: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    subscriptions,
    pending,
    cancelModal,
    user,
    toggleCancelModal,
    toggleCancelProTrialModal,
    cancelProTrialModal,
    isExpired,
    isCanceled,
    canCancel,
    formatDate,
    refetchSubscriptions,
    onUpdateRenewal,
    loading,
  } = useSubscriptionModel();

  const navigateToUpgrade = (planCode?: string) => {
    if (user?.plan?.planCode?.includes("coach+")) {
      return history.push("/upgrade?plan=coachpro+yearly");
    }

    const path = planCode
      ? `/upgrade?plan=${encodeURIComponent(planCode)}`
      : "/upgrade";
    return history.push(path);
  };

  // Athlete-side accounts (athletes, parents/fans) trial Analyze, everyone
  // else Pro (mirrors usePaywall routing).
  const userIsAthleteSide = ["athlete", "other"].includes(
    user?.kind?.toLowerCase(),
  );
  const navigateToTrial = () => {
    history.push(userIsAthleteSide ? "/trial/analyze" : "/trial/pro");
  };

  const handleCancelSuccess = () => {
    if (refetchSubscriptions) {
      refetchSubscriptions();
    }
  };

  // renders the dates of a subscription based on type and state
  const renderSubscriptionDates = (subscription: SubscriptionModel) => {
    const { isAutoRenewing, expiresAt } = subscription;
    const { plan } = user || {};
    const isCanceledTrial =
      !isAutoRenewing &&
      expiresAt &&
      plan?.canceledAt &&
      plan?.status === "trial";

    // A trial (pro or academy) auto-converts to a paid subscription when it ends,
    // so show that instead of a renewal date. isStripeUser excludes licensed
    // academy subs, so gate on the auto-renewing trial state directly. Use the
    // subscription's expiresAt (like the other branches) — plan.expiresAt can be
    // absent for licensed academy subs.
    if (isAutoRenewing && plan?.status === "trial" && expiresAt) {
      return (
        <p>
          <Trans
            i18nKey="Trial expires on <strong>{{date}}</strong>. Subscription will start automatically after the trial ends."
            components={{ strong: <strong /> }}
            values={{ date: formatDate(expiresAt) }}
          />
        </p>
      );
    }

    // auto-renewing subscription
    if (isAutoRenewing && expiresAt) {
      return (
        <p className={cls(styles.plan_info)}>
          {t("Renews on")}: {formatDate(expiresAt)}
        </p>
      );
    }

    // canceled trial
    if (isCanceledTrial) {
      return (
        <p>
          <Trans
            i18nKey="<strong>Status</strong>: {{status}} <br /> Trial Expires on {{date}}"
            components={{ strong: <strong />, br: <br /> }}
            values={{ date: formatDate(expiresAt!), status: "Canceled" }}
          />
        </p>
      );
    }

    // non-auto-renewing with expiry date
    if (!isAutoRenewing && expiresAt) {
      const expired = isExpired(subscription);
      return (
        <p className={cls(styles.plan_info, expired ? "text-danger" : "")}>
          {expired ? t("Expired on") : t("Expires on")} {formatDate(expiresAt)}
        </p>
      );
    }

    return null;
  };

  // renders what a subscription is: its dates, and who pays for it
  const renderSubscriptionInfo = (subscription: SubscriptionModel) => (
    <>
      {renderSubscriptionDates(subscription)}
      {hasLicensedPlan(user) && (
        <p className={cls(styles.plan_info)}>
          {t("Provided by {{name}}", {
            name: user?.plan?.organizationName || t("your organization"),
          })}
        </p>
      )}
    </>
  );

  const renderResubscribeButton = (subscription: SubscriptionModel) => (
    <Button
      title={t("Re-subscribe")}
      buttonType="success"
      buttonStyles={styles.resusbscribe_btn}
      disabled={loading || pending}
      onClickFunc={() =>
        onUpdateRenewal({
          product: subscription,
          enable: !subscription.isAutoRenewing,
        })
      }
    />
  );

  // A plan that was cancelled but still runs: the API reports it as "canceled"
  // until its period ends and "expired" after, so "canceled" is a live plan
  // here — the coach can still resume it.
  const isCanceledLivePlan = (subscription: SubscriptionModel) => {
    const status = user?.plan?.status?.toLowerCase();
    return (
      (isCanceled(subscription) || !!user?.plan?.canceledAt) &&
      (status === "trial" || status === "active" || status === "canceled")
    );
  };

  // renders action button based on subscription and user state
  const renderActionButton = (subscription: SubscriptionModel) => {
    // A licensed seat is paid for by a subscription that is not the coach's
    // own. The API lets only the coach who bought the organization's pool, or
    // the organization's owner, end or resume it (plan.canCancel); everyone
    // else gets no billing control here — only the plan, its dates and who
    // provides it.
    if (hasLicensedPlan(user)) {
      if (!canCancelLicensedPlan(user)) {
        return null;
      }

      return isCanceledLivePlan(subscription) ? (
        renderResubscribeButton(subscription)
      ) : (
        <Button
          title={t("Cancel")}
          buttonType="danger"
          onClickFunc={toggleCancelProTrialModal}
        />
      );
    }

    const isStripe = isStripeUser(user);
    const hasProTrial = hasActiveProTrial(user);

    // show re-subscribe for canceled subscriptions
    if (isCanceledLivePlan(subscription)) {
      return renderResubscribeButton(subscription);
    }

    // stripe users can only cancel
    if (isStripe || hasProTrial) {
      return (
        <div className={styles.start_trial_btns}>
          <Button
            title={t("Cancel")}
            buttonType="danger"
            onClickFunc={toggleCancelProTrialModal}
          />

          {user?.plan?.planCode?.includes("coach+") && (
            <Button
              title={t("Upgrade")}
              buttonType="primary"
              onClickFunc={() => navigateToUpgrade(subscription.planCode)}
            />
          )}
        </div>
      );
    }

    // regular cancel
    if (canCancel(subscription)) {
      return (
        <Button
          title={t("Cancel")}
          buttonType="danger"
          onClickFunc={toggleCancelModal}
        />
      );
    }

    // upgrade
    if (isCanceled(subscription) || hasExpiredCoachPlusTrial(user)) {
      return (
        <Button
          title={t("Upgrade")}
          buttonType="primary"
          onClickFunc={() => navigateToUpgrade(subscription.planCode)}
        />
      );
    }

    return null;
  };

  // renders empty state when no subscriptions. The upgrade page sells coach
  // plans, so athletes only get the trial CTA.
  const renderEmptyState = () => (
    <div className={styles.plan_container}>
      <div className={styles.plan_wrapper}>
        <h4 className={styles.plan_title}>
          {userIsAthleteSide ? t("CoachNow Analyze") : t("CoachNow Pro")}
        </h4>
      </div>
      <div className={styles.start_trial_btns}>
        <Button
          title={t("Start Trial")}
          buttonType="success"
          onClickFunc={navigateToTrial}
        />
        {!userIsAthleteSide && (
          <Button
            title={t("Upgrade")}
            buttonType="primary"
            onClickFunc={() => navigateToUpgrade()}
          />
        )}
      </div>
    </div>
  );

  // renders a single subscription item
  const renderSubscriptionItem = (subscription: SubscriptionModel) => (
    <div key={subscription._id} className={styles.plan_container}>
      <div className={styles.plan_wrapper}>
        <h4 className={styles.plan_title}>{subscription.planName}</h4>
        {renderSubscriptionInfo(subscription)}
      </div>
      {renderActionButton(subscription)}
      {cancelProTrialModal && (
        <CancelProTrialSubscriptionModal
          subscription={subscription}
          // Falls back like the rest of the licensed copy: without a name the
          // modal would drop the warning that cancelling ends every seat of
          // the pool, which is the one thing he must read before confirming.
          organizationName={
            hasLicensedPlan(user)
              ? user?.plan?.organizationName || t("your organization")
              : undefined
          }
          isOpen={cancelProTrialModal}
          onClose={toggleCancelProTrialModal}
          onCancelSuccess={handleCancelSuccess}
        />
      )}
      {cancelModal && (
        <SubscriptionCancelModal
          subscription={subscription}
          isOpen={cancelModal}
          onClose={toggleCancelModal}
        />
      )}
    </div>
  );

  // main render logic
  const renderContent = () => {
    if (pending) {
      return (
        <Loading
          loadType="spinner"
          isLoading={pending}
          data-testid="subscriptions_loader"
        />
      );
    }

    const hasNoSubscriptions = !subscriptions || subscriptions.length === 0;
    const hasNotCanceled = !user?.plan?.canceledAt;

    if (hasNoSubscriptions && hasNotCanceled) {
      return renderEmptyState();
    }

    return subscriptions?.map(renderSubscriptionItem);
  };

  return (
    <AccountWrap>
      <div className={cls(styles.container, "panel")}>
        <h1 className={styles.title}>{t("Subscriptions")}</h1>
        <div className={styles.subscriptions_list_container}>
          {renderContent()}
        </div>
      </div>
    </AccountWrap>
  );
};

export default Subscriptions;
